<template>
    <div class="ssml-editor" :class="{ 'ssml-editor--disabled': isDisabled }">
        <transition name="fade">
            <div v-if="isDisabled" class="ssml-editor__disabled">
                Can't edit SSML, please select speaker
            </div>
        </transition>
        <div class="ssml-editor__row">
            <button type="button" class="ssml-editor__top-button" @click="selectAll">
                {{ $t('editor.ssml.selectAll') }}
            </button>
            <button
                v-if="mode !== 'visual'"
                type="button"
                class="ssml-editor__top-button"
                @click="setMode('visual')"
            >
                {{ $t('editor.ssml.modes.visual') }}
            </button>
            <button
                v-if="mode === 'visual'"
                type="button"
                class="ssml-editor__top-button"
                @click="setMode('source')"
            >
                {{ $t('editor.ssml.modes.source') }}
            </button>
        </div>
        <div
            v-if="mode === 'visual'"
            class="ssml-editor__field ssml-visual"
            :class="{ rtl: isRtl }"
            @click.self="clearSelection"
        >
            <Spinner v-if="loading" />
            <div class="ssml-visual__wrap">
                <SsmlTreeNode v-if="ssmlTree" :node="ssmlTree" :tree-height="treeHeight" />
            </div>
        </div>
        <div v-if="mode === 'source'" class="ssml-editor__hard-edit">
            <textarea
                ref="textarea"
                v-model="ssmlString"
                :dir="isRtl ? 'rtl' : 'auto'"
                class="ssml-editor__field"
                rows="4"
            />
        </div>
        <div class="ssml-editor__row">
            <div class="ssml-editor__tip">
                {{ $t('editor.ssml.tip') }}
            </div>
            <portal-target name="ssml-editor-after-text" class="ssml-editor__portal" multiple />
        </div>
        <portal v-if="selection.length && active" to="toolbar">
            <div class="toolbar__group">
                <div class="ssml-visual__top">
                    <a href="" class="ssml-editor__back" @click.prevent="back">
                        {{ $t('editor.toolbar.backButton') }}
                    </a>
                    <button
                        v-if="selection.length === 1 && selection[0].type === 'tag'"
                        type="button"
                        class="button button--blank ssml-editor__back"
                        @click.prevent="unwrapNodes(selection[0])"
                    >
                        Clear all settings
                    </button>
                </div>

                <MassReplacement
                    class="ssml-editor__mass-replacement"
                    :selection="selection"
                    :speaker="data.speaker"
                    :speaker-settings="speakerSettings"
                    @update="onMassChange"
                />

                <SsmlBaseControl
                    v-if="isPauseSelected"
                    :key="selection[0].id"
                    :speaker="data.speaker.slug"
                    :tag="ssmlTags.break"
                    :nodes="selection"
                    @update="updateNode"
                    @wrapNodes="wrapNodes"
                    @unwrapNodes="unwrapNodes"
                />
                <template v-else-if="selection.length === 1 && selection[0].type === 'tag'">
                    <SsmlBaseControl
                        :key="selection[0].id"
                        :tag="ssmlTags[selection[0].name]"
                        :nodes="selection"
                        :speaker="data.speaker.slug"
                        @update="updateNode"
                        @wrapNodes="wrapNodes"
                        @unwrapNodes="unwrapNodes"
                    />
                </template>
                <template v-else>
                    <SsmlBaseControl
                        v-for="tag in filteredTags"
                        :key="tag.name"
                        :tag="tag"
                        :nodes="selection"
                        :speaker="data.speaker.slug"
                        @update="updateNode"
                        @wrapNodes="wrapNodes"
                        @unwrapNodes="unwrapNodes"
                    />
                </template>
                <div v-if="!filteredTags.length">
                    No settings available
                </div>
            </div>
        </portal>
    </div>
</template>

<script>
import { mapGetters, mapActions, mapState } from 'vuex';

import { makeid } from '@speechki-frontend/shared/helpers/functions';

import SsmlTreeNode from '../SsmlTreeNode/SsmlTreeNode';
import MassReplacement from '../MassReplacement/MassReplacement';
import SsmlBaseControl from '../SsmlBaseControl/SsmlBaseControl';

import SSMLParser, { calcTreeHeight, fixChildrenLevels } from '../../services/ssmlParser';
import { isRtlLanguage } from '@speechki-frontend/core/editor/utils';

export default {
    name: 'SsmlEditor',
    components: {
        SsmlTreeNode,
        MassReplacement,
        SsmlBaseControl
    },
    provide() {
        return {
            selectNode: this.selectNode,
            unselectNode: this.unselectNode,
            clearSelection: this.clearSelection,
            getSortedSelection: () => {
                return this.sortedSelection;
            },
            isSelected: this.isSelected,
            isSelectable: this.isSelectable
        };
    },
    props: {
        data: {
            type: Object,
            required: true
        },
        active: {
            type: Boolean,
            default: false
        },
        speaker: {
            type: Object,
            required: true
        },
        speakerSettings: {
            type: Object,
            default: () => {}
        }
    },
    data() {
        return {
            mode: 'visual',
            loading: false,
            afterCancel: false,
            selection: [],
            ssmlTree: undefined,
            ssmlString: '',
            treeHeight: 1,
            initialParse: true,
            prevSsmlString: '',

            parser: undefined
        };
    },
    computed: {
        ...mapGetters(['phonemes', 'bookLanguage', 'speakerObject']),
        ...mapGetters('ssmlTags', ['availableTags']),
        ...mapState('ssmlTags', {
            ssmlTags: state => state.ssmlTags
        }),
        selectedMultiple() {
            return this.selection.length > 1;
        },
        isPauseSelected() {
            return (
                this.selection.length === 1 &&
                (this.selection[0].name === 'break' || this.selection[0].name === 'space')
            );
        },
        sortedSelection() {
            return this.selection
                .slice()
                .sort((a, b) => a.parent.children.indexOf(a) - b.parent.children.indexOf(b));
        },
        multipleSelection() {
            return this.selection.length > 1;
        },
        isDisabled() {
            return !Object.keys(this.speakerSettings).length;
        },

        //
        filteredTags() {
            const tags = Object.values(this.ssmlTags)
                .filter(
                    tag =>
                        tag.name !== 'break' &&
                        (tag.is_one_word_tag ? !this.multipleSelection : true)
                )
                .sort((a, b) => a.sort - b.sort);

            if (this.selection.length) {
                let [{ parent }] = this.selection;

                if (!parent) {
                    parent = {};
                }

                if (parent.type === 'tag' && parent.name !== 'speak') {
                    return this.ssmlTags[parent.name].is_nested_tags ? tags : [];
                }
            }

            return tags;
        },
        isRtl() {
            return isRtlLanguage(this.bookLanguage);
        }
    },
    watch: {
        ssmlString() {
            if (this.initialParse) {
                this.initialParse = false;
                return;
            }
            this.prevSsmlString = this.ssmlString;

            this.$emit('ssmlChanged', `<speak>${this.ssmlString || ''}</speak>`);
        },

        'speaker.slug': {
            async handler(newv, oldv) {
                this.afterCancel = false;
                if (newv && newv !== oldv) {
                    this.loading = true;
                    let oldSsmlTags = this.availableTags.slice();
                    await this.fetchSsmlTags(newv);

                    // // check if speaker available tags are different, then clear all unsupported tags
                    if (oldSsmlTags.join('') !== this.availableTags.join('')) {
                        this.runSsmlCheck(oldv);
                    }

                    this.loading = false;
                }
            }
        },

        // TODO: this could probably be optimized
        'data.id'() {
            this.parser = new SSMLParser(this.speakerSettings);
            this.runParse();
        },

        speakerSettings: {
            deep: true,
            handler() {
                this.parser = new SSMLParser(this.speakerSettings);
                this.runParse();
            }
        },

        ssmlTree: {
            deep: true,
            handler() {
                this.loading = true;
                this.ssmlString = this.parser.build(this.ssmlTree);
                this.treeHeight = calcTreeHeight(this.ssmlTree);
                this.loading = false;
            }
        },

        active(value) {
            if (value) return;

            this.$nextTick(() => {
                this.clearSelection();
            });
        },

        selection: {
            deep: true,
            handler(value) {
                if (value.length) {
                    this.$emit('opened');
                } else {
                    this.$emit('closed');
                }
            }
        }
    },
    async created() {
        this.initialParse = true;
        this.loading = true;
        await this.fetchSsmlTags(this.data.speaker.slug);

        this.$store.subscribeAction(action => {
            if (
                action.type === 'bookpart/resetBookpart' ||
                action.type === 'bookpart/setBookpart' ||
                action.type === 'bookpart/fetchBookpart'
            ) {
                if (action.type === 'bookpart/fetchBookpart' && action.payload === this.data.id) {
                    return;
                }

                this.initialParse = true;
            }
        });

        // runparse finishes loading
        this.prevSsmlString = this.data.ssml_text;
        this.parser = new SSMLParser(this.speakerSettings);
        this.runParse();
    },
    mounted() {
        // the toolbar dispatches window events alongside its emits because
        // $parent.$on is gone in Vue 3 (see EditorToolbar cancel/save)
        this.onToolbarCancel = () => {
            this.$nextTick(() => {
                this.afterCancel = true;
                this.clearSelection();
                this.runParse();
                this.$emit('closed');
            });
        };
        this.onToolbarSave = () => {
            if (this.data.ssml_text !== this.prevSsmlString || this.mode === 'source') {
                this.clearSelection();
                this.runParse();
            }
        };
        window.addEventListener('ssml-editor-cancel', this.onToolbarCancel);
        window.addEventListener('ssml-editor-save', this.onToolbarSave);
    },

    beforeUnmount() {
        window.removeEventListener('ssml-editor-cancel', this.onToolbarCancel);
        window.removeEventListener('ssml-editor-save', this.onToolbarSave);
        this.parser = null;
    },

    methods: {
        ...mapActions('ssmlTags', ['fetchSsmlTags']),
        setMode(mode) {
            this.mode = mode;
        },
        back() {
            this.$emit('closed');
            this.clearSelection();
        },
        runParse() {
            this.loading = true;
            this.ssmlTree = this.parser.parse(this.data.ssml_text);
            this.treeHeight = calcTreeHeight(this.ssmlTree);
            this.loading = false;
        },
        updateNode({ changes, node }) {
            Object.keys(changes).forEach(change => {
                node[change] = changes[change];
            });
        },
        wrapNodes(changes) {
            const parent = this.selection[0].parent;
            const firstIndex = parent.children.indexOf(this.sortedSelection[0]);
            const lastIndex = parent.children.indexOf(
                this.sortedSelection[this.selection.length - 1]
            );

            const nodesToWrap = parent.children
                .slice()
                .splice(firstIndex, lastIndex - firstIndex + 1);

            const node = {
                id: makeid(),
                parent: parent,
                level: this.selection[0].level,
                children: nodesToWrap,
                ...changes
            };

            fixChildrenLevels(node);

            parent.children.splice(firstIndex, lastIndex - firstIndex + 1, node);

            this.clearSelection();
            this.selectNode(node);
        },
        unwrapNodes(node = this.selection[0]) {
            const parent = node.parent;
            let nodesToUnwrap = node.children;

            // IMPORTANT! hack for break as it cant have children on parsing
            // but needs to be unwrapped to space
            if (node.name === 'break') {
                nodesToUnwrap = [
                    {
                        attributes: {},
                        id: makeid(),
                        level: node.level + 1,
                        parent: node,
                        type: 'space',
                        name: 'space'
                    }
                ];
            }

            const index = parent.children.indexOf(node);

            parent.children.splice(index, 1, ...nodesToUnwrap);

            fixChildrenLevels(parent);

            const filteredNodesToUnwrap = nodesToUnwrap
                .slice()
                .filter(node => node.name !== 'space' && node.name !== 'break');

            this.setSelection(filteredNodesToUnwrap);
        },
        runSsmlCheck(oldSpeaker) {
            if (this.afterCancel) return;

            const { extra_params } = this.speakerObject(oldSpeaker);
            const symbol = extra_params && extra_params.accent_symbol;

            this.checkSsmlNode(this.ssmlTree, symbol);
        },
        checkSsmlNode(node, accentSymbol) {
            let { type, name, children, source, text } = node;

            if (type === 'tag' && !this.availableTags.includes(name) && name !== 'speak') {
                this.unwrapNodes(node);
            }

            if (accentSymbol && source && type !== 'number') {
                const regexp = new RegExp(accentSymbol.replace(/\+/g, '\\$&'), 'g');
                node['source'] = source.replace(regexp, '');
                node['text'] = text.replace(regexp, '');
            }

            if (children) {
                children.forEach(child => {
                    this.checkSsmlNode(child, accentSymbol);
                });
            }
        },

        onMassChange() {
            this.$emit('massChange');
        },

        // selection methods
        selectAll() {
            this.clearSelection();
            this.ssmlTree.children.forEach(child => {
                if (child.name !== 'break' && child.name !== 'space') {
                    this.selectNode(child);
                }
            });
        },
        selectNode(node) {
            if (this.selection.includes(node)) {
                return;
            }

            this.selection.push(node);
        },
        unselectNode(node) {
            let index = this.selection.indexOf(node);
            if (index > -1) {
                this.selection.splice(index, 1);
            }
        },
        clearSelection() {
            this.selection = [];
        },
        setSelection(nodes) {
            this.selection = nodes;
        },
        isSelected(node) {
            return this.selection.includes(node);
        },
        isSelectable(node) {
            if (!this.selection.length) {
                return true;
            }

            if (node.type === 'word' && node.parent.children.length === 1) {
                return this.selection[0].parent.children.includes(node.parent);
            }

            return this.selection[0].parent.children.includes(node);
        }
    }
};
</script>

<style lang="scss" src="./SsmlEditor.scss"></style>
